import { z } from 'zod';
import { AwardTypeCode, OperationalAwardTypeCode, StageId } from './awards.js';
import { Cents, PlainDate } from './common.js';

const Ref = z.object({ id: z.string(), name: z.string() });
const PersonRef = Ref.extend({ initials: z.string() });
const AwardRef = z.object({ id: z.string(), displayId: z.string() });
const Count = z.number().int().nonnegative();
const Month = z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/, 'must be YYYY-MM');
const DateTime = z.iso.datetime({ offset: true });

/**
 * An award holding a pool (counted from submission, PRD §9): the pool card's In progress list.
 * It holds what the pool's own figures count as held (APP-088): its lines Payroll hasn't paid yet.
 */
export const PoolAwardHold = z.object({
  kind: z.literal('award'),
  award: AwardRef,
  type: AwardTypeCode,
  people: Count,
  program: z.string().nullable(),
  /** What this award still holds of this pool: its lines on it that aren't paid yet. */
  cashCents: Cents,
  options: Count,
  stage: StageId,
  /** Over budget and sent to the Executives: by how much cash. */
  overBudgetCents: Cents.nullable(),
  /** The signed-in person must act on it (it's also in their Waiting on you). */
  waitingOnYou: z.boolean(),
});
export type PoolAwardHold = z.infer<typeof PoolAwardHold>;

/**
 * The Spot awards holding the pool, as one line (APP-083): how many and their cash, so the list
 * adds up to the pool's figure, with no award, name or nomination, which stay private until
 * they're released.
 */
export const PoolSpotHold = z.object({
  kind: z.literal('spot_total'),
  awards: Count,
  cashCents: Cents,
});
export type PoolSpotHold = z.infer<typeof PoolSpotHold>;

/**
 * The awards past the list's first 20, as one line (APP-088): how many and what they hold, so the
 * list always adds up to the pool's figure in progress.
 */
export const PoolMoreHold = z.object({
  kind: z.literal('more_awards'),
  awards: Count,
  cashCents: Cents,
});
export type PoolMoreHold = z.infer<typeof PoolMoreHold>;

export const PoolHold = z.discriminatedUnion('kind', [PoolAwardHold, PoolSpotHold, PoolMoreHold]);
export type PoolHold = z.infer<typeof PoolHold>;

/** 0 = nothing that month, 1–4 = darker squares for more recognition (by cash). */
const Intensity = z.number().int().min(0).max(4);

/**
 * A sector's people over the last 12 months (SL1). An award counts once it's past approval
 * (with the Stock Admin team, paid or closed); entries waiting on approval don't.
 */
export const TeamSnapshot = z.object({
  people: Count,
  teams: Count,
  recognized: Count,
  repeat: Count,
  notYet: Count,
  /** The repeat-recipient rule (a setting): this many awards, or this much, in 12 months. */
  repeatRule: z.object({ awards: Count, cashCents: Cents }),
  /** The last 12 months, oldest first. */
  months: z.array(z.object({ month: Month, awards: Count, cashCents: Cents })).length(12),
  /** About 8 rows, most recent award first; the full team view has everyone. */
  rows: z
    .array(
      z.object({
        person: PersonRef,
        team: z.string(),
        months: z.array(Intensity).length(12),
        awards: Count,
        status: z.enum(['repeat', 'recent', 'earlier', 'none']),
      }),
    )
    .max(10),
  mostRecognized: z
    .array(
      z.object({
        person: PersonRef,
        team: z.string(),
        awards: Count,
        cashCents: Cents,
        lastMonth: Month,
        repeat: z.boolean(),
      }),
    )
    .max(5),
  notRecognized: z.object({
    count: Count,
    sample: z.array(PersonRef).max(9),
    biggestTeams: z.array(z.object({ team: z.string(), count: Count })).max(2),
  }),
});
export type TeamSnapshot = z.infer<typeof TeamSnapshot>;

/** One sector the person leads or enters awards for (HOME1). The pool numbers come from /budgets. */
export const SectorRecognition = z.object({
  sector: Ref,
  /** Awards holding the sector's pool, newest first. A delegate sees the ones they entered. */
  holds: z.array(PoolHold),
  /** Minimum options per person when options are given (a setting). */
  minimumOptionsPerPerson: Count,
  /** Null for a delegate: the sector dashboard is the Sector Lead's (Q16 default). */
  team: TeamSnapshot.nullable(),
});
export type SectorRecognition = z.infer<typeof SectorRecognition>;

export const MyAward = z.object({
  /** Exact employee line; award.id remains the containing request ID. */
  lineId: z.uuid(),
  award: AwardRef,
  /** A Spot award is the person's own too, once it's released (APP-083). */
  type: OperationalAwardTypeCode,
  awardDate: PlainDate,
  program: z.string().nullable(),
  cashCents: Cents,
  options: Count,
  stage: StageId,
  /** The election deadline while the cash-or-stock choice is open. */
  electionDue: PlainDate.nullable(),
  documents: z.array(
    z.object({ kind: z.enum(['letter', 'earning_statement']), date: PlainDate, href: z.string() }),
  ),
});
export type MyAward = z.infer<typeof MyAward>;

export const AreteValue = z.enum([
  'creativity',
  'integrity',
  'passion',
  'responsiveness',
  'collaboration',
]);

export const SpotNomination = z.object({
  id: z.string(),
  nominee: Ref,
  value: AreteValue,
  status: z.enum(['draft', 'sent', 'awarded', 'not_awarded']),
  updatedAt: DateTime,
});
export type SpotNomination = z.infer<typeof SpotNomination>;

/** The personal section: E1 for employees, last on every blended Home. */
export const MyRecognition = z.object({
  /** The person's own awards from their award date, newest first. */
  awards: z.array(MyAward),
  totals: z.object({
    awards: Count,
    since: z.number().int().nullable(),
    options: Count,
    cashPaidCents: Cents,
  }),
  spot: z.object({
    nominations: z.array(SpotNomination),
    /** The Spot cash range, or null when it isn't set. */
    cashRange: z.object({ minCents: Cents, maxCents: Cents }).nullable(),
  }),
  /** Who to ask about an award, or null when no one is named. */
  contact: z.object({ person: PersonRef, title: z.string() }).nullable(),
  /** "Check your details" on the first visit. */
  details: z.object({ joined: PlainDate.nullable(), manager: Ref.nullable() }),
  /**
   * The days an employee has to choose cash or stock, from the award date: the election window
   * setting for a PAA, today. The first-visit card states it (FR1). Optional: older servers omit it.
   */
  electionWindowDays: z.number().int().min(1).optional(),
});
export type MyRecognition = z.infer<typeof MyRecognition>;

/** `GET /api/v1/home/recognition`. */
export const HomeRecognition = z.object({
  asOf: PlainDate,
  /** One per sector the person leads or enters for. */
  sectors: z.array(SectorRecognition),
  mine: MyRecognition,
});
export type HomeRecognition = z.infer<typeof HomeRecognition>;
